import type { ModuleId } from '../types';

/** Slug for CSS `data-value` on themed selects (spaces → underscores). */
export function toSelectSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export type DeptIconTone =
  | 'amber'
  | 'blue'
  | 'green'
  | 'red'
  | 'teal'
  | 'purple'
  | 'orange'
  | 'pink'
  | 'slate';

export interface DeptTheme {
  slug: string;
  label: string;
  iconTone: DeptIconTone;
}

/** Product process-step departments — matches fulfillment + dispatch workflow. */
export const WORKFLOW_DEPARTMENTS = [
  'Accounts',
  'Design',
  'Production',
  'Finishing',
  'Installation',
  'Quality Check',
  'Logistics',
] as const;

const NON_PRODUCT_PROCESS_STEP_DEPARTMENTS = new Set(['Accounts', 'Installation', 'Logistics']);

/** Admin product editor — fulfillment steps only; invoicing and dispatch are outside product workflow. */
export const PRODUCT_PROCESS_STEP_DEPARTMENTS = WORKFLOW_DEPARTMENTS.filter(
  (d) => !NON_PRODUCT_PROCESS_STEP_DEPARTMENTS.has(d),
);

/** Canonical department themes (badges, page icons, section accents) */
export const DEPT_THEMES: Record<string, DeptTheme> = {
  management: { slug: 'management', label: 'Management', iconTone: 'slate' },
  sales: { slug: 'sales', label: 'Sales', iconTone: 'blue' },
  accounts: { slug: 'accounts', label: 'Accounts', iconTone: 'green' },
  design: { slug: 'design', label: 'Design', iconTone: 'purple' },
  production: { slug: 'production', label: 'Production', iconTone: 'orange' },
  finishing: { slug: 'finishing', label: 'Finishing', iconTone: 'pink' },
  quality_check: { slug: 'quality_check', label: 'Quality Check', iconTone: 'teal' },
  installation: { slug: 'installation', label: 'Installation', iconTone: 'blue' },
  dispatch: { slug: 'dispatch', label: 'Dispatch', iconTone: 'amber' },
  logistics: { slug: 'logistics', label: 'Logistics', iconTone: 'orange' },
};

/** Department slug → display label */
export const DEPT_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(DEPT_THEMES).map((t) => [t.slug, t.label]),
);

export function departmentSlug(name: string): string {
  return toSelectSlug(name);
}

export function getDeptTheme(department: string): DeptTheme {
  const slug = departmentSlug(department);
  return DEPT_THEMES[slug] ?? { slug, label: department, iconTone: 'slate' };
}

export function getDeptIconClass(department: string): string {
  return `dept-icon-${getDeptTheme(department).iconTone}`;
}

export function getDeptBadgeClass(department: string): string {
  return `dept-badge--${getDeptTheme(department).slug}`;
}

/** App module permissions — colors align with operations departments */
export const MODULE_IDS: ModuleId[] = [
  'orders',
  'accounts',
  'design',
  'production',
  'finishing',
  'qc',
  'dispatch',
  'installation',
  'reports',
  'admin',
  'settings',
];

export const MODULE_LABELS: Record<ModuleId, string> = {
  orders: 'Orders',
  accounts: 'Accounts',
  design: 'Design',
  production: 'Production',
  finishing: 'Finishing',
  qc: 'Quality Check',
  dispatch: 'Dispatch',
  installation: 'Installation',
  reports: 'Reports',
  admin: 'Admin',
  settings: 'Settings',
};

/** Maps module id → department slug for shared badge/select colors */
const MODULE_DEPT_SLUG: Partial<Record<ModuleId, string>> = {
  orders: 'sales',
  accounts: 'accounts',
  design: 'design',
  production: 'production',
  finishing: 'finishing',
  qc: 'quality_check',
  dispatch: 'dispatch',
  installation: 'installation',
  reports: 'management',
  admin: 'management',
  settings: 'management',
};

export function getModuleBadgeClass(moduleId: ModuleId): string {
  const deptSlug = MODULE_DEPT_SLUG[moduleId] ?? 'management';
  return `module-badge--${moduleId} dept-badge--${deptSlug}`;
}

/** User role badge classes (Admin users table) */
export const ROLE_BADGE_CLASSES: Record<string, string> = {
  admin: 'admin-role-admin',
  sales: 'admin-role-sales',
  accounts: 'admin-role-accounts',
  design_manager: 'admin-role-design-mgr',
  designer: 'admin-role-designer',
  production: 'admin-role-production',
  qc: 'admin-role-qc',
  dispatch: 'admin-role-dispatch',
};

export function getRoleBadgeClass(roleId: string): string {
  return ROLE_BADGE_CLASSES[roleId] ?? 'admin-role-default';
}

/** Payment mode → semantic badge class */
export function getPaymentModeBadgeClass(mode: string): string {
  return `semantic-badge--${mode}`;
}
